import { labelOf } from './dimensions';
import type { MentionRow } from './generator';
import { postCopy } from './pageConversations';
import { detectedSentiment, rowId, type RowId } from './sentimentOverrides';

/**
 * Top performing content — every mention in the filtered set as a **post**,
 * ranked by the interactions it drew.
 *
 * Across every brand the `Brands` chip allows, not just ours (settled with the
 * user 2026-09-18). Competitors is the one module where the chip is live, and a
 * "top content" list that ignored it would be the only thing on the page that
 * did.
 */

export type PostRow = {
  /**
   * The mention's stable corpus identity — the same across renders, filters and
   * periods, which is what a sentiment correction has to be keyed on.
   */
  id: RowId;
  /**
   * Position by interactions, 1-based — fixed to the post, **not** to wherever
   * the table's current sort puts it. Re-sorting by sentiment must not renumber
   * the posts, or "#1" would stop meaning "the post that drew the most".
   */
  rank: number;
  /**
   * `Robi · Network coverage complaint on Facebook` — the composed line the
   * `Tune sentiment` modal names a post by. See `postSummary`.
   */
  summary: string;
  /**
   * What the post *says*, for the table's `Conversations` column.
   *
   * The same invented copy `Page conversations` shows, from `postCopy`, rather
   * than `summary`: the 2026-09-23 recut renamed the column `Post summary` →
   * `Conversations` and gave Channel, Intent and Topic columns of their own,
   * so a summary that spells all three out again would repeat three of its
   * neighbours and say nothing the row does not already show.
   */
  conversation: string;
  brand: string;
  channel: string;
  channelLabel: string;
  intentLabel: string;
  topicLabel: string;
  /** Where the post lives. See `postUrl` — it is a fixture, not a scraped link. */
  url: string;
  /** Reactions **including likes** — `PageRow.reactions`' fold, for its reason. */
  reactions: number;
  comments: number;
  shares: number;
  /** The sentiment the app currently counts — detected, or corrected if tuned. */
  sentiment: string;
  /** What the classifier said, whatever has been corrected since. */
  detected: string;
  interactions: number;
  /** This post's interactions as a percentage of every post in the set. */
  share: number;
};

/**
 * What the post was about, in one line, built from the mention's own fields.
 *
 * The corpus carries no post text — it is structured mentions, not scraped
 * copy — and the frame's "Olivia Rhye" is a placeholder from the component's
 * own example. So the summary is composed: brand, then what was said about
 * what, then where. `Robi · Network coverage complaint on Facebook`.
 *
 * The brand leads because the table has no Brand column and ranks every
 * brand's posts together: without it, a reader cannot tell our best post from
 * a competitor's.
 */
export function postSummary(row: MentionRow): string {
  const brand = labelOf('brand', row.brand);
  const topic = labelOf('topic', row.topic);
  const intent = labelOf('intent', row.intent).toLowerCase();
  const channel = labelOf('channel', row.channel);
  return `${brand} · ${topic} ${intent} on ${channel}`;
}

/**
 * A permalink for one post.
 *
 * **Invented, like the post copy beside it.** The corpus is structured
 * mentions, not scraped posts: no row carries a URL, which is why the
 * `Page URL → View` button on the table above this one has been a documented
 * no-op since it was built. The table's leading cell became a link on
 * 2026-09-23, and a link needs somewhere to go.
 *
 * Each one is shaped like the platform's real permalink — `/posts/`,
 * `/status/`, `urn:li:activity:` — and keyed on the brand's handle and the
 * post's own stable corpus id, so the same post always resolves to the same
 * URL and two posts never share one. **It will not open a real post**; swap
 * this function for the field when the corpus carries one.
 */
export function postUrl(row: MentionRow): string {
  const handle = row.brand;
  const id = rowId(row);

  switch (row.channel) {
    case 'facebook':
      return `https://www.facebook.com/${handle}/posts/${id}`;
    case 'instagram':
      return `https://www.instagram.com/p/${handle}-${id}/`;
    case 'x':
      return `https://x.com/${handle}/status/${id}`;
    case 'linkedin':
      return `https://www.linkedin.com/feed/update/urn:li:activity:${id}/`;
    case 'reddit':
      return `https://www.reddit.com/r/${handle}/comments/${id}/`;
    case 'youtube':
      return `https://www.youtube.com/watch?v=${handle}-${id}`;
    case 'tiktok':
      return `https://www.tiktok.com/@${handle}/video/${id}`;
    default:
      /* A channel with no known permalink shape still gets a unique, inert one. */
      return `https://example.com/${row.channel}/${handle}/${id}`;
  }
}

export function postList(rows: readonly MentionRow[]): PostRow[] {
  let total = 0;
  for (const row of rows) total += row.engagement;

  return (
    rows
      .map((row, index) => ({ row, index }))
      /*
       * Interactions first; then the newer post, then source order, so two
       * posts with equal interactions hold the same ranks on every render
       * rather than trading places.
       */
      .sort(
        (a, b) =>
          b.row.engagement - a.row.engagement || b.row.day - a.row.day || a.index - b.index,
      )
      .map(({ row }, position) => ({
        id: rowId(row),
        rank: position + 1,
        summary: postSummary(row),
        conversation: postCopy(row),
        url: postUrl(row),
        brand: row.brand,
        channel: row.channel,
        channelLabel: labelOf('channel', row.channel),
        intentLabel: labelOf('intent', row.intent),
        topicLabel: labelOf('topic', row.topic),
        reactions: row.reactions + row.likes,
        comments: row.comments,
        shares: row.shares,
        sentiment: row.sentiment,
        detected: detectedSentiment(row),
        interactions: row.engagement,
        share: total === 0 ? 0 : (row.engagement / total) * 100,
      }))
  );
}
